function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function isLikelyImageUrl(value = '') {
  const text = String(value || '').trim();
  if (!text) return false;
  return (
    text.startsWith('http://') ||
    text.startsWith('https://') ||
    text.startsWith('/') ||
    text.startsWith('data:image/') ||
    text.startsWith('blob:')
  );
}

function extractImageUrlDeep(input, depth = 0, seen = new Set()) {
  if (depth > 4 || input == null) return '';

  if (typeof input === 'string') {
    const value = input.trim();
    if (!value) return '';
    if (value.startsWith('{') || value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        return extractImageUrlDeep(parsed, depth + 1, seen);
      } catch (_) {
        return isLikelyImageUrl(value) ? value : '';
      }
    }
    return isLikelyImageUrl(value) ? value : '';
  }

  if (Array.isArray(input)) {
    for (const item of input) {
      const url = extractImageUrlDeep(item, depth + 1, seen);
      if (url) return url;
    }
    return '';
  }

  if (typeof input !== 'object') return '';
  if (seen.has(input)) return '';
  seen.add(input);

  const direct = [
    input.url,
    input.src,
    input.secure_url,
    input.imageUrl,
    input.image_url,
    input.downloadUrl,
    input.download_url,
  ]
    .map((value) => String(value || '').trim())
    .find((value) => isLikelyImageUrl(value));
  if (direct) return direct;

  const nestedFirst = [input.image, input.images, input.file, input.asset, input.data];
  for (const entry of nestedFirst) {
    const nestedUrl = extractImageUrlDeep(entry, depth + 1, seen);
    if (nestedUrl) return nestedUrl;
  }

  for (const [key, value] of Object.entries(input)) {
    if (!value) continue;
    const normalizedKey = String(key || '').toLowerCase();
    if (normalizedKey.includes('url') || normalizedKey.includes('src') || normalizedKey.includes('image')) {
      const nestedUrl = extractImageUrlDeep(value, depth + 1, seen);
      if (nestedUrl) return nestedUrl;
    }
  }

  return '';
}

function normalizeCropRect(rect) {
  if (!rect || typeof rect !== 'object') return null;
  const x = Number(rect.x);
  const y = Number(rect.y);
  const width = Number(rect.width);
  const height = Number(rect.height);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
    return null;
  }
  const normalizedWidth = clamp(Number(width.toFixed(6)), 0.0001, 1);
  const normalizedHeight = clamp(Number(height.toFixed(6)), 0.0001, 1);
  const normalizedX = clamp(Number(x.toFixed(6)), 0, 1 - normalizedWidth);
  const normalizedY = clamp(Number(y.toFixed(6)), 0, 1 - normalizedHeight);
  return {
    x: normalizedX,
    y: normalizedY,
    width: normalizedWidth,
    height: normalizedHeight,
  };
}

export function getImageUrl(image) {
  return extractImageUrlDeep(image);
}

export function normalizeImageCrop(crop) {
  if (!crop || typeof crop !== 'object') return null;
  const zoom = Number(crop.zoom);
  const x = Number(crop.x);
  const y = Number(crop.y);
  if (!Number.isFinite(zoom) || !Number.isFinite(x) || !Number.isFinite(y)) return null;
  const rect = normalizeCropRect(crop.rect);
  return {
    zoom: clamp(Number(zoom.toFixed(4)), 0.5, 3),
    x: clamp(Number(x.toFixed(4)), -1.5, 1.5),
    y: clamp(Number(y.toFixed(4)), -1.5, 1.5),
    ...(rect ? { rect } : {}),
  };
}

export function getImageCrop(image) {
  if (!image || typeof image !== 'object') return null;
  return normalizeImageCrop(image.crop);
}

export function normalizeImageEntry(image) {
  const url = getImageUrl(image);
  if (!url) return null;
  const crop = getImageCrop(image);
  return crop ? { url, crop } : { url };
}

export function normalizeImageList(images) {
  if (!Array.isArray(images)) return [];
  return images.map(normalizeImageEntry).filter(Boolean);
}

export function buildImageTransformStyle(image, extraStyle = {}) {
  const crop = getImageCrop(image);
  if (!crop) return extraStyle;
  if (crop.rect) {
    return {
      ...extraStyle,
      width: '100%',
      height: '100%',
      maxWidth: 'none',
      transform: `translate(-${(crop.rect.x / crop.rect.width) * 100}%, -${(crop.rect.y / crop.rect.height) * 100}%) scale(${1 / crop.rect.width}, ${1 / crop.rect.height})`,
      transformOrigin: 'top left',
    };
  }
  return {
    ...extraStyle,
    transform: `translate(calc(-50% + ${crop.x * 100}%), calc(-50% + ${crop.y * 100}%)) scale(${crop.zoom})`,
    transformOrigin: 'center center',
  };
}
